import { headerCase } from 'change-case';

import {
  convertTokenValue,
  isEmpty,
  isFunction,
  notEmpty,
  prettyNumber,
  reverseArray,
} from '../utils/core';

const getKey = ({
  eventName,
  ercType,
  status,
  subtype,
}) => ({
  NotToppedUp: () => 'NotToppedUp',
  FeesCollected: () => 'FeesCollected',
  ProposalEvent: () => `ProposalEvent.${status}`,
  ProposalVote: () => `ProposalVote.${status}`,
  Deposit: () => 'Deposit',
  Transfer: () => `Transfer.${subtype || 'fee'}`,
  TransferSingle: () => `TransferSingle.${subtype || 'fee'}`,
  Approval: () => `Approval.${subtype || (ercType === '20' ? 'bridgeTollBooth' : 'bridge')}`,
}[eventName] || (() => 'unknown'))();

const ercLogSteps = {
  default: {
    NotToppedUp: {
      hidden: true,
      label: 'Top up: Not required',
    },
    FeesCollected: {
      hidden: true,
      label: ({ chain }) =>
        `Bridge fee collected by Bridge Toll Booth contract on ${chain}`,
    },
    'ProposalEvent.executed': {
      label: 'Transfer completed',
    },
    'ProposalEvent.passed': {
      hidden: true,
      label: ({ chain }) => `Voting passed and transfer proposal approved on ${chain}`,
    },
    'ProposalEvent.active': {
      hidden: true,
      label: ({ chain }) => `Transfer proposal set to active via Bridge contract on ${chain}`,
    },
    'ProposalVote.active': {
      hidden: true,
      label: ({ chain }) => `Vote of approval for proposal via Bridge contract on ${chain}`,
    },
    'Transfer.mint': {
      label: ({ chain, value, symbol }) =>
        `<b>${value} ${symbol}</b> <i>minted</i> and transferred to wallet on ${chain} `,
    },
    'TransferSingle.mint': {
      label: ({ chain, value, symbol, tokenId }) =>
        `<b>${value} ${symbol}</b> with TokenID <b>${tokenId}</b> <i>minted</i> and transferred to wallet on ${chain} `,
    },
    'Transfer.release': {
      label: ({ chain, value, symbol }) =>
        `<b>${value} ${symbol}</b> <i>released</i> to wallet from Bridge Handler contract on ${chain} `,
    },
    'TransferSingle.release': {
      label: ({ chain, value, symbol, tokenId }) =>
        // eslint-disable-next-line
        `<b>${value} ${symbol}</b> with TokenID <b>${tokenId}</b> <i>released</i> to wallet from Bridge Handler contract on ${chain} `,
    },
    'Transfer.lock': {
      label: ({ chain, value, symbol }) =>
        `<b>${value} ${symbol}</b> transferred from wallet and <i>locked</i> in Bridge Handler contract on ${chain}`,
    },
    'TransferSingle.lock': {
      label: ({ chain, value, symbol, tokenId }) =>
        // eslint-disable-next-line
        `<b>${value} ${symbol}</b> with TokenID <b>${tokenId}</b> transferred from wallet and <i>locked</i> in Bridge Handler contract on ${chain}`,
    },
    'Transfer.burn': {
      label: ({ chain, value, symbol }) =>
        `<b>${value} ${symbol}</b> <i>burnt</i> from wallet on ${chain}`,
    },
    'TransferSingle.burn': {
      label: ({ chain, value, symbol, tokenId }) =>
        `<b>${value} ${symbol}</b> with TokenID <b>${tokenId}</b> <i>burnt</i> from wallet on ${chain}`,
    },
    'Transfer.fee': {
      label: ({ chain, value, symbol }) =>
        `<b>${value} ${symbol}</b> Bridge fee transferred from wallet to Bridge Toll Booth contract on ${chain}`,
    },
    'Approval.bridge': {
      hidden: true,
      label: ({ chain }) =>
        `Bridge contract approved for token transfer from wallet on ${chain}`,
    },
    // Unsure if this one exists but just in case
    'Approval.handler': {
      hidden: true,
      label: ({ chain }) =>
        `Bridge Handler contract approved for transfer on ${chain}`,
    },
    'Approval.bridgeTollBooth': {
      hidden: true,
      label: ({ chain }) =>
        `Bridge Toll Booth contract approved for transfer on ${chain}`,
    },
    Deposit: {
      hidden: true,
      label: ({ chain }) => `Bridge contract Deposit method invoked on ${chain}`,
    },
  },
};

const ercStepperStates = {
  default: {
    base: [
      { label: 'Process', order: 1 },
      { label: 'Approve', order: 2 },
      { label: 'Transfer', order: 3 },
    ],
    states: {
      // If NotToppedUp occurs, it will be after completion
      NotToppedUp: [
        { label: 'Initiated', completed: true },
        { label: 'Approved', completed: true },
        { label: 'Transferred', completed: true },
      ],
      'ProposalEvent.executed': [
        { label: 'Initiated', completed: true },
        { label: 'Approved', completed: true },
        { label: 'Transferred', completed: true },
      ],
      'Transfer.release': [
        { label: 'Initiated', completed: true },
        { label: 'Approved', completed: true },
        { label: 'Transferring ...', inProgress: true },
      ],
      'Transfer.mint': [
        { label: 'Initiated', completed: true },
        { label: 'Approved', completed: true },
        { label: 'Transferring ...', inProgress: true },
      ],
      'ProposalEvent.passed': [
        { label: 'Initiated', completed: true },
        { label: 'Approved', completed: true },
      ],
      'ProposalVote.active': [
        { label: 'Initiated', completed: true },
        { label: 'Approving ...', inProgress: true },
      ],
      'ProposalEvent.active': [
        { label: 'Initiated', completed: true },
        { label: 'Approving ...', inProgress: true },
      ],
      // Burn or Lock signifies or a Deposit the start of the transfer
      'Transfer.burn': [{ label: 'Initiated', completed: true }],
      'Transfer.lock': [{ label: 'Initiated', completed: true }],
      Deposit: [{ label: 'Initiated', completed: true }],
      'Approval.bridge': [{ label: 'Initiating ...', inProgress: true }],
      'Approval.handler': [{ label: 'Initiating ...', inProgress: true }],
      'Approval.bridgeTollBooth': [{ label: 'Initiating ...', inProgress: true }],
    },
  },
};

const getLogStep = (log) => {
  const { ercType, eventName } = log;
  const key = getKey(log);
  const fallback = { label: `${eventName} event` };
  const {
    default: fallbackErc,
    [`erc${ercType}`]: logSteps = fallbackErc,
  } = ercLogSteps;
  const logStep = logSteps[key] || fallback;
  return { key, ...logStep };
};

const getSimpleLogSteps = ({ ercType, eventName, status = '' }) => {
  const key = getKey({ eventName, status });
  const fallback = [{ label: 'Initiating (loading) ...', inProgress: true }];
  const {
    default: fallbackErc,
    [`erc${ercType}`]: { base = [], states = {} } = fallbackErc,
  } = ercStepperStates;
  const { [key]: state = fallback } = states;
  const simpleSteps = base.map((baseState, i) => ({ ...baseState, ...(state[i] || {}) }));
  return simpleSteps;
};

const parseLogForLabel = ({
  token: {
    units,
    symbol,
    tokenId,
  } = {},
  chain,
}) => ({
  chain: headerCase(chain),
  value: parseFloat(units).toString(),
  symbol,
  tokenId,
});

export const getFullSteps = (transfer = {}) => {
  const { logs = [], token: { ercType } = {} } = transfer;
  if (isEmpty(logs)) {
    return { latestStepIndex: 0, steps: [] };
  }
  const steps = logs
    .map((log, index) => {
      const { eventName, status, blockCreated, ...rest } = log;
      // Strip decimals here rather than doing for each label function
      const logStep = getLogStep({ eventName, status, ercType, ...rest });
      const { label: labelFn } = logStep;
      const label = isFunction(labelFn) ? labelFn(parseLogForLabel(log), index, logs) : labelFn;
      return {
        ...(notEmpty(logStep) && { completed: true }),
        ...logStep,
        label,
        blockCreated,
        eventName,
        status,
        ...rest,
        index,
      };
    });
  const latestStepIndex = steps.reduce((i, { completed, inProgress }, idx) => (completed || inProgress) ? idx : i, 0);
  return {
    latestStepIndex,
    latestStep: steps[latestStepIndex],
    steps,
  };
};

export const getSimpleSteps = (transfer = {}) => {
  const {
    logs = [],
    pending = false,
    token: { ercType } = {},
  } = transfer;
  const [latestLog] = logs.slice(-1);
  const simpleSteps = getSimpleLogSteps({ ercType, ...latestLog });
  if (!latestLog) {
    // single pending event means initiated
    const steps = pending
      ? [{ label: 'Initiating ...', inProgress: true }, ...simpleSteps.slice(1)]
      : [];
    return { latestStepIndex: 0, latestStep: steps[0], steps };
  }
  const steps = Object.entries(simpleSteps)
    .map(([key, step]) => ({ key, ...step }))
    .sort(({ order: a }, { order: b }) => a - b);
  const latestStepIndex = reverseArray(steps).reduce((i, { completed, inProgress }, idx) =>
    (inProgress || completed) ? idx > 0 ? i : (steps.length - 1) - idx : 0, 0);
  return {
    latestStepIndex,
    latestStep: steps[latestStepIndex],
    steps,
  };
};

export const calculateBridgeFee = (greenFee = 0, relayFee = 0) =>
  convertTokenValue(parseFloat(greenFee) + parseFloat(relayFee), 18).toString();

// @TODO - this is probably not appropriate for the UI but does represent the range of gas prices
export const calculateGasPriceRange = (events) =>
  Object.fromEntries(Object.entries(events.reduce((acc, {
    gasPrice,
    gwei = gasPrice ? convertTokenValue(parseFloat(gasPrice), 9) : undefined,
  }) =>
    ({
      minPrice: acc.minPrice ? Math.min(gwei, acc.minPrice) : gwei,
      maxPrice: acc.maxPrice ? Math.max(gwei, acc.maxPrice) : gwei,
    }), {})).map(([k, v]) => ([k, prettyNumber(v)])));
